import React, {
  ReactNode,
  useCallback,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import { useIntl } from '@kyruus/intl';
import { Box, type BoxProps } from '@biome-design-system/ui/Box';
import { Drawer, type DrawerProps } from '@biome-design-system/ui/Drawer';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { CloseIcon } from '@biome-design-system/icons/CloseIcon';

import { scrollDrawerIntoViewOnMobile } from 'Src/utils/drawerScroll';
import { messages } from './messages';

interface SideSheetProps
  extends Pick<DrawerProps, 'variant' | 'anchor'>,
    Pick<BoxProps, 'width'> {
  /** The id of the element used to title the side sheet */
  ariaLabelledBy: string;
  children: ReactNode;
  dataTestId?: string;
  footer?: ReactNode;
  header: ReactNode;
  id: string;
  onClose: (e: SyntheticEvent, reason: string) => void;
  /** On mobile, scroll the side sheet into view while it's open */
  scrollIntoViewOnMobile?: boolean;
}

/**
 * Side sheets are surfaces containing supplementary content that are
 * anchored to the left or right edge of the screen.
 */
export const SideSheet = ({
  anchor = 'right',
  ariaLabelledBy,
  children,
  dataTestId,
  footer,
  header,
  id,
  onClose,
  scrollIntoViewOnMobile = false,
  width,
  variant = 'temporary'
}: SideSheetProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    // delay the opening of the drawer to allow the animation to work properly
    setTimeout(() => {
      setOpen(true);
    }, 1);
  }, []);

  useEffect(() => {
    if (!open || !scrollIntoViewOnMobile) {
      return undefined;
    }
    return scrollDrawerIntoViewOnMobile(id);
  }, [id, open, scrollIntoViewOnMobile]);

  const closeButtonRef = useCallback((closeButtonNode) => {
    if (closeButtonNode) {
      closeButtonNode.focus();
    }
  }, []);

  return (
    <Drawer
      id={id}
      data-testid={dataTestId}
      anchor={anchor}
      onClose={onClose}
      open={open}
      variant={variant}
      aria-labelledby={ariaLabelledBy}
      role="dialog"
    >
      <Box
        width={width}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          'body.in-iframe &': { height: 'auto' }
        }}
      >
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 3,
            py: 2
          })}
        >
          {header}
          <IconButton
            onClick={(e: SyntheticEvent) => onClose(e, 'closeClick')}
            variant="outlined"
            data-testid="close-drawer-button"
            ref={closeButtonRef}
            aria-label={intl.formatMessage(messages.closeDrawer)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, padding: 3, overflowY: 'auto' }}>
          {children}
        </Box>
        {footer && (
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              background: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              px: 3,
              py: 2
            })}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
